import { Box, Grid, Stack, Typography } from "@mui/material";
import type { QuestionType, QuizResponse } from "@verity/database/prisma";
import DOMPurify from "dompurify";
import { ReactNode, useState } from "react";
import i18n from "../../../../../../i18n";
import styles from "./FlaggedQuestion.module.css";
import { ArrowIcon, ChevronDownIcon, ChevronUpIcon, CorrectIcon, IncorrectIcon, LightbulbIcon } from "./assets";

type TrueFalseOption = { text: "True" | "False"; weightage: number };
type MultipleChoiceOption = { text: string; weightage: number };
type MatchingOption = { premise: string; response: string; weightage: number };
type FillInTheBlankOption = { blankId: string; answers: string[]; weightage: number };
type OrderingOption = { id: string; text: string; order: number };

type QuestionSnapshot =
	| { questionType: "TrueFalse"; questionText: string; options: TrueFalseOption[] }
	| { questionType: "MultipleChoice"; questionText: string; options: MultipleChoiceOption[] }
	| { questionType: "Matching"; questionText: string; options: MatchingOption[] }
	| { questionType: "FillInTheBlank"; questionText: string; options: FillInTheBlankOption[] }
	| {
			questionType: Exclude<QuestionType, "TrueFalse" | "MultipleChoice" | "Matching" | "FillInTheBlank" | "Ordering">;
			questionText: string;
	  }
	| { questionType: "Ordering"; questionText: string; options: OrderingOption[] };

// Enumerated by hand (rather than via the Prisma `QuestionType` enum object) because this file runs in the
// browser, where the Prisma client's runtime module isn't available — only its types are.
const QUESTION_TYPES: Record<QuestionType, true> = {
	MultipleChoice: true,
	TrueFalse: true,
	FillInTheBlank: true,
	Matching: true,
	FileUpload: true,
	Essay: true,
	Ordering: true,
	AudioRecording: true,
	FileSubmission: true,
	DragAndDrop: true,
	Dropdown: true,
	Spreadsheet: true,
	Coding: true,
};

function isQuestionType(value: unknown): value is QuestionType {
	return typeof value === "string" && value in QUESTION_TYPES;
}

/** `questionSnapshot` is stored as raw JSON; this recovers its shape based on the question type it was snapshotted from. */
function getQuestionSnapshot(question: QuizResponse): QuestionSnapshot | null {
	const snapshot = question.questionSnapshot;
	if (typeof snapshot !== "object" || snapshot === null || Array.isArray(snapshot)) return null;
	if (!isQuestionType(snapshot.questionType) || typeof snapshot.questionText !== "string") return null;

	return snapshot as QuestionSnapshot;
}

type FlaggedQuestionProps = {
	question: QuizResponse;
	submitGapTimeMs: number;
};

export default function FlaggedQuestion({ question, submitGapTimeMs }: FlaggedQuestionProps) {
	const [open, setOpen] = useState(false);

	if (!question.answer) return null;

	const snapshot = getQuestionSnapshot(question);
	if (!snapshot) return null;

	const questionNumber = question.questionNo;
	const KEY = "result.components.studentSelection.videoPlayer.flaggedQuestionTSX";

	let questionTypeString: string = "";
	let bannerText: string = "";
	let answerBodyContent: ReactNode = null;

	if (snapshot.questionType === "TrueFalse") {
		const answer = question.answer as string[];
		const options = snapshot.options;

		const answerIndex = parseInt(answer[0], 10);
		const correctAnswerIndex = options.findIndex((option) => option.weightage === 1);
		const isCorrect = answerIndex === correctAnswerIndex;

		questionTypeString = i18n.t(`${KEY}.questionTypeTrueFalse`) as string;
		bannerText = i18n.t(isCorrect ? `${KEY}.trueFalseBannerCorrect` : `${KEY}.trueFalseBannerWrong`, {
			seconds: Math.round(submitGapTimeMs / 1000),
		}) as string;

		answerBodyContent = (
			<Stack direction="column" gap="12px">
				<Box className={styles.answerBox}>
					<Typography>{i18n.t(`${KEY}.theirAnswerValue`, { answer: options[answerIndex].text })}</Typography>
					{isCorrect ? <CorrectIcon height="13px" /> : <IncorrectIcon height="13px" />}
				</Box>
			</Stack>
		);
	} else if (snapshot.questionType === "MultipleChoice") {
		const answer = question.answer as string[];
		const options = snapshot.options;

		const answerIndex = parseInt(answer[0], 10);
		const correctAnswerIndex = options.findIndex((option) => option.weightage === 1);
		const isCorrect = answerIndex === correctAnswerIndex;

		questionTypeString = i18n.t(`${KEY}.questionTypeMultipleChoice`) as string;
		bannerText = i18n.t(isCorrect ? `${KEY}.multipleChoiceBannerCorrect` : `${KEY}.multipleChoiceBannerWrong`, {
			seconds: Math.round(submitGapTimeMs / 1000),
		}) as string;

		answerBodyContent = (
			<Stack direction="column" gap="12px">
				<Box className={styles.answerBox}>
					<Typography>
						{i18n.t(`${KEY}.theirAnswerValue`, {
							answer: `${String.fromCharCode(65 + answerIndex)}) ${options[answerIndex].text}`,
						})}
					</Typography>
					{isCorrect ? <CorrectIcon height="13px" /> : <IncorrectIcon height="13px" />}
				</Box>
			</Stack>
		);
	} else if (snapshot.questionType === "Matching") {
		const answer = question.answer as Record<string, string>;
		const options = snapshot.options;
		const hasMistake = options.some((option) => answer[option.premise] !== option.response);

		questionTypeString = i18n.t(`${KEY}.questionTypeMatching`) as string;
		bannerText = i18n.t(hasMistake ? `${KEY}.matchingBannerWrong` : `${KEY}.matchingBannerCorrect`) as string;

		answerBodyContent = (
			<Stack direction="column" gap="12px">
				<Grid container alignItems="center" spacing={1}>
					<Grid item xs={4}>
						<Typography className={styles.answerHeader}>{i18n.t(`${KEY}.premise`)}</Typography>
					</Grid>
					<Grid item xs={4}>
						<Typography className={styles.answerHeader}>{i18n.t(`${KEY}.theirAnswer`)}</Typography>
					</Grid>
				</Grid>
				{options.map((option) => {
					const isCorrect = answer[option.premise] === option.response;

					return (
						<Box className={styles.answerBox} key={option.premise}>
							<Grid container alignItems="center" spacing={1}>
								<Grid item xs={4}>
									<Typography>{option.premise}</Typography>
								</Grid>
								<Grid item xs={4} display="flex" alignItems="center">
									<ArrowIcon height="12px" color="#737373" />
									<Typography>{answer[option.premise]}</Typography>
								</Grid>
								<Grid item xs={4} display="flex" justifyContent="flex-end" alignItems="center">
									{isCorrect ? <CorrectIcon height="13px" /> : <IncorrectIcon height="13px" />}
								</Grid>
							</Grid>
						</Box>
					);
				})}
			</Stack>
		);
	} else if (snapshot.questionType === "FillInTheBlank") {
		const answer = question.answer as { [blankId: string]: string };
		const options = snapshot.options;
		const multipleBlanks = options.length > 1;
		const hasMistake = options.some((option) => !option.answers.includes(answer[option.blankId]));

		questionTypeString = i18n.t(`${KEY}.questionTypeFillInBlank`) as string;
		const fillInBlankBannerKey = multipleBlanks
			? hasMistake
				? `${KEY}.fillInBlankBannerAllMistake`
				: `${KEY}.fillInBlankBannerAllCorrect`
			: hasMistake
				? `${KEY}.fillInBlankBannerSingleMistake`
				: `${KEY}.fillInBlankBannerSingleCorrect`;
		bannerText = i18n.t(fillInBlankBannerKey, { count: options.length }) as string;

		answerBodyContent = (
			<Stack direction="column" gap="12px">
				{options
					.sort((a, b) => parseInt(a.blankId) - parseInt(b.blankId))
					.map((option) => {
						const isCorrect = option.answers.includes(answer[option.blankId]);

						return (
							<Box className={styles.answerBox} key={option.blankId}>
								<Typography>
									{i18n.t(`${KEY}.blankLabel`, { blankId: option.blankId })}
									{answer[option.blankId]}
								</Typography>
								{isCorrect ? <CorrectIcon height="13px" /> : <IncorrectIcon height="13px" />}
							</Box>
						);
					})}
			</Stack>
		);
	} else if (snapshot.questionType === "Ordering") {
		const answer = question.answer as string[];
		const options = snapshot.options.sort((a, b) => a.order - b.order);
		const isCorrect = answer.every((a, index) => a === options[index].id);

		questionTypeString = i18n.t(`${KEY}.questionTypeOrdering`) as string;
		bannerText = i18n.t(isCorrect ? `${KEY}.orderingBannerCorrect` : `${KEY}.orderingBannerIncorrect`) as string;

		answerBodyContent = (
			<Stack direction="column" gap="12px">
				<Typography className={styles.answerHeader}>{i18n.t(`${KEY}.theirSubmittedOrder`)}</Typography>
				{answer.map((a, index) => {
					const isCorrect = index === options.findIndex((o) => o.id === a);

					return (
						<Box className={styles.answerBox} key={a}>
							<Typography>
								{i18n.t(`${KEY}.positionLabel`, {
									position: index + 1,
									answer: options.find((option) => option.id === a)?.text,
								})}
							</Typography>
							{isCorrect ? <CorrectIcon height="13px" /> : <IncorrectIcon height="13px" />}
						</Box>
					);
				})}
			</Stack>
		);
	}

	return (
		<Box className={styles.box}>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Typography className={styles.questionText}>
					{i18n.t(`${KEY}.questionLabel`, {
						number: questionNumber,
					})}
					{questionTypeString} ·{" "}
					<Typography
						component="span"
						className={styles.questionText}
						margin={0}
						dangerouslySetInnerHTML={{
							__html: DOMPurify.sanitize(
								snapshot.questionText
									.replace("<p>", "<span>")
									.replace("</p>", "</span>")
									.replace(/\(Blank (\d+)\)/g, "__($1)__"),
							),
						}}
					/>
				</Typography>
				<button
					type="button"
					aria-label={open ? "open" : "close"}
					onClick={() => setOpen(!open)}
					className={styles.buttonOverride}
					style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24 }}
				>
					{open ? <ChevronUpIcon /> : <ChevronDownIcon />}
				</button>
			</Stack>
			{!open && <Typography className={styles.closedBannerText}>{bannerText}</Typography>}
			{open && (
				<Stack gap="8px">
					<Box className={styles.openBanner}>
						<LightbulbIcon height="16px" width="16px" />
						<Typography>{bannerText}</Typography>
					</Box>
					{answerBodyContent}
				</Stack>
			)}
		</Box>
	);
}
